/**
 * ClubPage — main club page with header + tabs.
 *
 * Route: /clubs/:clubId
 *
 * Tabs: Files | Leaderboard | Predictions | Members
 * Activity feed at the bottom.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  EmojiEvents as LeaderboardIcon,
  Psychology as PredictionsIcon,
  Group as MembersIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  AdminPanelSettings as AdminIcon,
  PersonRemove as PersonRemoveIcon,
  Add as AddIcon,
  Insights as AnalyzeIcon,
  CompareArrows as CompareIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import {
  getClub,
  getClubFiles,
  getClubMembers,
  getPendingRequests,
  approveRequest,
  rejectRequest,
  removeMember,
  updateMemberRole,
  removeClubFile,
  analyzeClubFile,
  downloadClubFile,
  contributeFile,
  getClubLeaderboard,
  getClubPredictions,
  getClubEvents,
  updateClub,
  deleteClub,
  regenerateInviteLink,
} from '../api/clubApi';
import WorkoutPickerDrawer from '../components/running/WorkoutPickerDrawer';
import { getRunningPrediction } from '../api/runningApi';

// ── Formatting helpers ──────────────────────────────────────────────────────

const fmtDist = (m) => {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
};

const fmtDuration = (secs) => {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtPace = (minKm) => {
  if (minKm == null) return '—';
  const m = Math.floor(minKm);
  const s = Math.round((minKm - m) * 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtEventText = (e) => {
  const who = e.user_email || 'Someone';
  const d = e.event_data || {};
  switch (e.event_type) {
    case 'file_contributed': {
      const dist = d.total_distance ? ` — ${fmtDist(d.total_distance)}` : '';
      const dur = d.total_duration ? ` in ${fmtDuration(d.total_duration)}` : '';
      return `${who} contributed ${d.filename || 'a file'}${dist}${dur}`;
    }
    case 'member_approved':
      return `${who} joined the club`;
    case 'join_requested':
      return `${who} requested to join`;
    case 'prediction_shared':
      return `${who} shared a prediction${d.race_name ? ` for ${d.race_name}` : ''}`;
    case 'comparison_created':
      return `${who} ran a comparison`;
    case 'member_left':
      return `${who} left the club`;
    case 'member_removed':
      return `${who} was removed from the club`;
    case 'club_created':
      return `Club "${d.club_name || ''}" was created`;
    default:
      return `${who} did something`;
  }
};

// ── Tab panel ───────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return value === index ? <Box mt={2}>{children}</Box> : null;
}

// ── Main component ──────────────────────────────────────────────────────────

export default function ClubPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  // Tab data
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [predsLoading, setPredsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Analyze loading state (club_file id being analyzed)
  const [analyzingId, setAnalyzingId] = useState(null);
  // Prediction view/compare loading state (prediction_id being loaded)
  const [viewingPredId, setViewingPredId] = useState(null);

  // Contribute drawer
  const [contributeOpen, setContributeOpen] = useState(false);

  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editColor, setEditColor] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const isAdmin = club?.my_role === 'admin';
  const myUserId = user?.id;

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadClub = useCallback(async () => {
    try {
      const data = await getClub(parseInt(clubId));
      setClub(data);
      setEditName(data.name);
      setEditDesc(data.description || '');
      setEditColor(data.color || '#4CAF50');
    } catch (err) {
      if (err?.response?.status === 403) {
        setError('You are not a member of this club.');
      } else if (err?.response?.status === 404) {
        setError('Club not found.');
      } else {
        setError('Failed to load club.');
      }
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const data = await getClubFiles(parseInt(clubId));
      setFiles(data);
    } finally {
      setFilesLoading(false);
    }
  }, [clubId]);

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const [m, p] = await Promise.all([
        getClubMembers(parseInt(clubId)),
        isAdmin ? getPendingRequests(parseInt(clubId)) : Promise.resolve([]),
      ]);
      setMembers(m);
      setPendingRequests(p);
    } finally {
      setMembersLoading(false);
    }
  }, [clubId, isAdmin]);

  const loadLeaderboard = useCallback(async () => {
    setLbLoading(true);
    try {
      const data = await getClubLeaderboard(parseInt(clubId));
      setLeaderboard(data);
    } finally {
      setLbLoading(false);
    }
  }, [clubId]);

  const loadPredictions = useCallback(async () => {
    setPredsLoading(true);
    try {
      const data = await getClubPredictions(parseInt(clubId));
      setPredictions(data);
    } finally {
      setPredsLoading(false);
    }
  }, [clubId]);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const data = await getClubEvents(parseInt(clubId));
      setEvents(data);
    } finally {
      setEventsLoading(false);
    }
  }, [clubId]);

  useEffect(() => { loadClub(); }, [loadClub]);

  useEffect(() => {
    if (!club) return;
    if (tab === 0) loadFiles();
    else if (tab === 1) loadLeaderboard();
    else if (tab === 2) loadPredictions();
    else if (tab === 3) loadMembers();
  }, [tab, club]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (club) loadEvents();
  }, [club]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Invite link ─────────────────────────────────────────────────────────

  const inviteUrl = club
    ? `${window.location.origin}/api/clubs/invite/${club.invite_token}/og`
    : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // ── Contribute file ──────────────────────────────────────────────────────

  const handleContribute = async (selected) => {
    if (!selected?.id) return;
    try {
      await contributeFile(parseInt(clubId), selected.id);
      setSnackMsg('File added to club library');
      loadFiles();
      loadEvents();
      loadClub();
    } catch (err) {
      setSnackMsg(err?.response?.data?.detail || 'Failed to contribute file');
    }
  };

  const handleRemoveFile = async (clubFileId) => {
    try {
      await removeClubFile(parseInt(clubId), clubFileId);
      setFiles((prev) => prev.filter((f) => f.id !== clubFileId));
      setSnackMsg('File removed from club');
    } catch (err) {
      setSnackMsg(err?.response?.data?.detail || 'Failed to remove file');
    }
  };

  /**
   * Fetch workout analysis for a club file, then navigate to RunningAnalysis.
   * @param {Object} row  — DataGrid row (ClubFile serialized)
   * @param {boolean} autoOpenCompare — if true, immediately open the comparison drawer
   */
  const handleAnalyzeFile = async (row, autoOpenCompare = false) => {
    setAnalyzingId(row.id);
    try {
      const workoutData = await analyzeClubFile(parseInt(clubId), row.id);
      navigate('/running/analysis', {
        state: {
          workoutData,
          clubContext: {
            clubId: parseInt(clubId),
            clubName: club.name,
            clubFileId: row.id,
            contributorEmail: row.contributor_email,
            filename: row.filename,
          },
          autoOpenCompare,
        },
      });
    } catch (err) {
      setSnackMsg(err?.response?.data?.detail || 'Failed to load analysis');
    } finally {
      setAnalyzingId(null);
    }
  };

  /**
   * Load a club prediction and navigate to RunningAnalysis to view it.
   * @param {Object} row — Predictions DataGrid row (ClubPrediction serialized)
   */
  const handleViewPrediction = async (row) => {
    setViewingPredId(row.id);
    try {
      const data = await getRunningPrediction(row.prediction_id);
      navigate('/running/analysis', {
        state: {
          predictionData: data,
          clubContext: {
            clubId: parseInt(clubId),
            clubName: club.name,
            clubPredictionId: row.id,
          },
        },
      });
    } catch (err) {
      setSnackMsg(err?.response?.data?.detail || 'Failed to load prediction');
    } finally {
      setViewingPredId(null);
    }
  };

  // ── Member actions ────────────────────────────────────────────────────────

  const handleApprove = async (userId) => {
    try {
      await approveRequest(parseInt(clubId), userId);
      loadMembers();
      loadClub();
      loadEvents();
    } catch (err) {
      setSnackMsg('Failed to approve request');
    }
  };

  const handleReject = async (userId) => {
    try {
      await rejectRequest(parseInt(clubId), userId);
      setPendingRequests((prev) => prev.filter((r) => r.user_id !== userId));
    } catch (err) {
      setSnackMsg('Failed to reject request');
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await removeMember(parseInt(clubId), userId);
      if (userId === myUserId) {
        navigate('/running');
      } else {
        loadMembers();
        loadClub();
      }
    } catch (err) {
      setSnackMsg('Failed to remove member');
    }
  };

  const handleToggleRole = async (m) => {
    const newRole = m.role === 'admin' ? 'member' : 'admin';
    try {
      await updateMemberRole(parseInt(clubId), m.user_id, newRole);
      loadMembers();
    } catch (err) {
      setSnackMsg('Failed to update role');
    }
  };

  // ── Settings ──────────────────────────────────────────────────────────────

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await updateClub(parseInt(clubId), { name: editName, description: editDesc, color: editColor });
      setSettingsOpen(false);
      loadClub();
      setSnackMsg('Club settings saved');
    } catch (err) {
      setSnackMsg(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleRegenerateInvite = async () => {
    try {
      await regenerateInviteLink(parseInt(clubId));
      loadClub();
      setSnackMsg('Invite link regenerated — old link is now invalid');
    } catch (err) {
      setSnackMsg('Failed to regenerate invite link');
    }
  };

  const handleDeleteClub = async () => {
    try {
      await deleteClub(parseInt(clubId));
      navigate('/running');
    } catch (err) {
      setSnackMsg('Failed to delete club');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const bgCard = theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[900];
  const bgPage = theme.palette.mode === 'dark' ? colors.grey[900] : '#1a1a2e';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/running')}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  // ── DataGrid column definitions ─────────────────────────────────────────

  const fileColumns = [
    { field: 'contributor_email', headerName: 'Member', flex: 1, minWidth: 120 },
    { field: 'filename', headerName: 'File', flex: 1.5, minWidth: 140 },
    { field: 'race_name', headerName: 'Race', flex: 1, minWidth: 100, valueFormatter: (value) => value || '—' },
    { field: 'workout_date', headerName: 'Date', width: 110, valueFormatter: (value) => fmtDate(value) },
    { field: 'total_distance', headerName: 'Distance', width: 90, valueFormatter: (value) => fmtDist(value) },
    { field: 'total_duration', headerName: 'Time', width: 90, valueFormatter: (value) => fmtDuration(value) },
    { field: 'avg_pace', headerName: 'Avg Pace', width: 100, valueFormatter: (value) => fmtPace(value) },
    {
      field: 'actions', headerName: '', width: 150, sortable: false,
      renderCell: (params) => {
        const isLoading = analyzingId === params.row.id;
        return (
          <Box display="flex" gap={0.5} alignItems="center">
            <Tooltip title="View analysis">
              <span>
                <IconButton
                  size="small"
                  disabled={isLoading}
                  onClick={(e) => { e.stopPropagation(); handleAnalyzeFile(params.row, false); }}
                  sx={{ color: colors.blueAccent[400], '&:hover': { color: colors.blueAccent[200] } }}
                >
                  {isLoading
                    ? <CircularProgress size={14} />
                    : <AnalyzeIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Compare with my file">
              <span>
                <IconButton
                  size="small"
                  disabled={isLoading}
                  onClick={(e) => { e.stopPropagation(); handleAnalyzeFile(params.row, true); }}
                  sx={{ color: colors.greenAccent?.[400] || '#4caf50', '&:hover': { color: colors.greenAccent?.[200] || '#81c784' } }}
                >
                  <CompareIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="Download FIT file">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); downloadClubFile(club.id, params.row.id, params.row.filename); }}
                  sx={{ color: colors.grey[400], '&:hover': { color: colors.blueAccent[300] } }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || params.row.contributed_by === myUserId) && (
              <Tooltip title="Remove from club">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleRemoveFile(params.row.id); }}
                  sx={{ color: colors.grey[500], '&:hover': { color: colors.redAccent?.[400] || '#f44336' } }}
                >
                  <PersonRemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const lbColumns = [
    { field: 'rank', headerName: '#', width: 50 },
    { field: 'contributor_email', headerName: 'Member', flex: 1, minWidth: 140 },
    { field: 'race_name', headerName: 'Race', flex: 1, minWidth: 100, valueFormatter: (value) => value || '—' },
    { field: 'workout_date', headerName: 'Date', width: 110, valueFormatter: (value) => fmtDate(value) },
    { field: 'total_duration', headerName: 'Finish Time', width: 110, valueFormatter: (value) => fmtDuration(value) },
    { field: 'avg_pace', headerName: 'Avg Pace', width: 100, valueFormatter: (value) => fmtPace(value) },
    { field: 'total_distance', headerName: 'Distance', width: 90, valueFormatter: (value) => fmtDist(value) },
  ];

  const predColumns = [
    { field: 'sharer_email', headerName: 'Member', flex: 1, minWidth: 140 },
    { field: 'race_name', headerName: 'Race', flex: 1, minWidth: 120, valueFormatter: (value) => value || '—' },
    {
      field: 'predicted_total_time_s', headerName: 'Predicted Time', width: 130,
      valueFormatter: (value) => fmtDuration(value),
    },
    { field: 'predicted_avg_pace', headerName: 'Avg Pace', width: 100, valueFormatter: (value) => fmtPace(value) },
    { field: 'race_distance_km', headerName: 'Distance', width: 90, valueFormatter: (value) => value ? `${value.toFixed(1)} km` : '—' },
    { field: 'shared_at', headerName: 'Shared', width: 110, valueFormatter: (value) => fmtDate(value) },
    {
      field: 'pred_actions', headerName: '', width: 80, sortable: false,
      renderCell: (params) => {
        const isLoading = viewingPredId === params.row.id;
        return (
          <Box display="flex" gap={0.5} alignItems="center">
            <Tooltip title="View prediction">
              <span>
                <IconButton
                  size="small"
                  disabled={isLoading}
                  onClick={(e) => { e.stopPropagation(); handleViewPrediction(params.row); }}
                  sx={{ color: colors.blueAccent[400], '&:hover': { color: colors.blueAccent[200] } }}
                >
                  {isLoading
                    ? <CircularProgress size={14} />
                    : <ViewIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const gridSx = {
    border: 'none',
    '& .MuiDataGrid-cell': { borderColor: colors.grey[700] },
    '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[700], borderColor: colors.grey[600] },
    '& .MuiDataGrid-footerContainer': { borderColor: colors.grey[700] },
    '& .MuiDataGrid-row:hover': { backgroundColor: `${colors.blueAccent[900]}40` },
  };

  return (
    <Box m={isMobile ? '10px' : '20px'} maxWidth={1200} mx="auto">

      {/* ── Club header ── */}
      <Box
        mb={2}
        p={isMobile ? 2 : 3}
        borderRadius={2}
        sx={{
          backgroundColor: bgCard,
          borderLeft: `6px solid ${club.color || colors.blueAccent[400]}`,
        }}
      >
        {/* Back + actions row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/running')}
            size="small"
            sx={{ textTransform: 'none', color: colors.grey[300] }}
          >
            Dashboard
          </Button>
          <Box display="flex" gap={1}>
            <Tooltip title={linkCopied ? 'Copied!' : 'Copy invite link'}>
              <Button
                size="small"
                variant="outlined"
                startIcon={linkCopied ? <CheckIcon /> : <LinkIcon />}
                onClick={handleCopyLink}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.grey[600],
                  color: colors.grey[200],
                  '&:hover': { borderColor: colors.blueAccent[400] },
                }}
              >
                {isMobile ? '' : 'Invite'}
              </Button>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="Club settings">
                <IconButton size="small" onClick={() => setSettingsOpen(true)}>
                  <SettingsIcon sx={{ color: colors.grey[400] }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Club identity */}
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: club.color || colors.blueAccent[400],
              flexShrink: 0,
            }}
          />
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
            {club.name}
          </Typography>
          {isAdmin && (
            <Chip label="Admin" size="small" sx={{ backgroundColor: colors.blueAccent[800], color: colors.grey[100], height: 20 }} />
          )}
        </Box>

        {club.description && (
          <Typography variant="body2" color={colors.grey[300]} ml={3} mb={0.5}>
            {club.description}
          </Typography>
        )}

        <Typography variant="caption" color={colors.grey[400]} ml={3}>
          {club.member_count} member{club.member_count !== 1 ? 's' : ''} · {club.file_count} file{club.file_count !== 1 ? 's' : ''}
          {isAdmin && club.pending_count > 0 && (
            <Chip
              label={`${club.pending_count} pending`}
              size="small"
              color="warning"
              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
              onClick={() => setTab(3)}
            />
          )}
        </Typography>
      </Box>

      {/* ── Tabs ── */}
      <Box sx={{ backgroundColor: bgCard, borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${colors.grey[700]}`,
            '& .MuiTab-root': { textTransform: 'none', minWidth: 80 },
          }}
        >
          <Tab icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Files" />
          <Tab icon={<LeaderboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Leaderboard" />
          <Tab icon={<PredictionsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Predictions" />
          <Tab
            icon={
              <Box position="relative" display="inline-flex">
                <MembersIcon sx={{ fontSize: 18 }} />
                {isAdmin && club.pending_count > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -6,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#f44336',
                    }}
                  />
                )}
              </Box>
            }
            iconPosition="start"
            label="Members"
          />
        </Tabs>

        <Box p={isMobile ? 1.5 : 2}>

          {/* ── Files tab ── */}
          <TabPanel value={tab} index={0}>
            <Box display="flex" gap={1} mb={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setContributeOpen(true)}
                sx={{ textTransform: 'none', borderColor: colors.blueAccent[700], color: colors.blueAccent[400] }}
              >
                Contribute from My Files
              </Button>
            </Box>

            {filesLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : files.length === 0 ? (
              <Box textAlign="center" py={5}>
                <FolderIcon sx={{ fontSize: 48, color: colors.grey[600], mb: 1 }} />
                <Typography color={colors.grey[400]} variant="body2" mb={2}>
                  No files yet — contribute your first workout or invite members.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleCopyLink}
                  sx={{ textTransform: 'none', borderColor: colors.grey[600], color: colors.grey[300] }}
                >
                  Copy invite link
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={files}
                columns={fileColumns}
                autoHeight
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={gridSx}
                disableRowSelectionOnClick
              />
            )}
          </TabPanel>

          {/* ── Leaderboard tab ── */}
          <TabPanel value={tab} index={1}>
            {lbLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : leaderboard.length === 0 ? (
              <Box textAlign="center" py={5}>
                <LeaderboardIcon sx={{ fontSize: 48, color: colors.grey[600], mb: 1 }} />
                <Typography color={colors.grey[400]} variant="body2">
                  Add files to start competing! Rankings appear automatically when files are contributed.
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={leaderboard}
                columns={lbColumns}
                autoHeight
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={gridSx}
                disableRowSelectionOnClick
              />
            )}
          </TabPanel>

          {/* ── Predictions tab ── */}
          <TabPanel value={tab} index={2}>
            {predsLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : predictions.length === 0 ? (
              <Box textAlign="center" py={5}>
                <PredictionsIcon sx={{ fontSize: 48, color: colors.grey[600], mb: 1 }} />
                <Typography color={colors.grey[400]} variant="body2">
                  Share your race predictions with the club to plan races together.
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={predictions}
                columns={predColumns}
                autoHeight
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={gridSx}
                disableRowSelectionOnClick
              />
            )}
          </TabPanel>

          {/* ── Members tab ── */}
          <TabPanel value={tab} index={3}>
            {membersLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : (
              <>
                {/* Pending requests — admin only */}
                {isAdmin && pendingRequests.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" color={colors.grey[300]} fontWeight="bold" mb={1}>
                      Pending Requests ({pendingRequests.length})
                    </Typography>
                    {pendingRequests.map((r) => (
                      <Box
                        key={r.id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={1.5}
                        mb={1}
                        borderRadius={1}
                        sx={{ backgroundColor: `${colors.blueAccent[900]}60`, border: `1px solid ${colors.blueAccent[700]}` }}
                      >
                        <Box>
                          <Typography variant="body2" color={colors.grey[200]}>{r.email}</Typography>
                          <Typography variant="caption" color={colors.grey[400]}>
                            Requested {fmtDate(r.requested_at)}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleApprove(r.user_id)}
                            sx={{ textTransform: 'none', minWidth: 80 }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleReject(r.user_id)}
                            sx={{ textTransform: 'none', minWidth: 70 }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </Box>
                    ))}
                    <Divider sx={{ mt: 2, mb: 2, borderColor: colors.grey[700] }} />
                  </Box>
                )}

                {/* Members list */}
                <Typography variant="subtitle2" color={colors.grey[300]} fontWeight="bold" mb={1}>
                  Members ({members.length})
                </Typography>
                {members.length === 1 ? (
                  <Box textAlign="center" py={4}>
                    <MembersIcon sx={{ fontSize: 48, color: colors.grey[600], mb: 1 }} />
                    <Typography color={colors.grey[400]} variant="body2" mb={2}>
                      You're the only member — invite your training buddies!
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={handleCopyLink}
                      sx={{ textTransform: 'none', borderColor: colors.grey[600], color: colors.grey[300] }}
                    >
                      Copy invite link
                    </Button>
                  </Box>
                ) : (
                  members.map((m) => (
                    <Box
                      key={m.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={1.5}
                      mb={1}
                      borderRadius={1}
                      sx={{ backgroundColor: colors.grey[700] }}
                    >
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color={colors.grey[100]}>{m.email}</Typography>
                          <Chip
                            label={m.role}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: m.role === 'admin' ? colors.blueAccent[700] : colors.grey[600],
                              color: colors.grey[100],
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color={colors.grey[400]}>
                          Joined {fmtDate(m.approved_at)}
                        </Typography>
                      </Box>

                      {isAdmin && m.user_id !== myUserId && (
                        <Box display="flex" gap={0.5}>
                          <Tooltip title={m.role === 'admin' ? 'Demote to member' : 'Promote to admin'}>
                            <IconButton size="small" onClick={() => handleToggleRole(m)}>
                              <AdminIcon sx={{ fontSize: 18, color: colors.grey[400] }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove from club">
                            <IconButton size="small" onClick={() => handleRemoveMember(m.user_id)}>
                              <PersonRemoveIcon sx={{ fontSize: 18, color: colors.grey[400] }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {m.user_id === myUserId && (
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => handleRemoveMember(m.user_id)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Leave
                        </Button>
                      )}
                    </Box>
                  ))
                )}
              </>
            )}
          </TabPanel>
        </Box>
      </Box>

      {/* ── Activity feed ── */}
      {events.length > 0 && (
        <Box mt={2} p={isMobile ? 1.5 : 2} borderRadius={2} sx={{ backgroundColor: bgCard }}>
          <Typography variant="subtitle2" color={colors.grey[300]} fontWeight="bold" mb={1.5}>
            Recent Activity
          </Typography>
          {eventsLoading ? (
            <CircularProgress size={20} />
          ) : (
            events.map((e) => (
              <Box key={e.id} display="flex" alignItems="flex-start" gap={1} mb={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: colors.blueAccent[400],
                    mt: '6px',
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography variant="body2" color={colors.grey[300]}>
                    {fmtEventText(e)}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[500]}>
                    {fmtDate(e.created_at)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* ── Contribute drawer ── */}
      <WorkoutPickerDrawer
        open={contributeOpen}
        onClose={() => setContributeOpen(false)}
        onSelect={(src) => {
          setContributeOpen(false);
          // src shape for myFiles: { type: 'myfile', fileId: number, record: Object }
          if (src?.fileId) handleContribute({ id: src.fileId });
        }}
        tabs={['myFiles']}
        title="Contribute a File"
        actionLabel="Contribute"
        footer={
          <Typography variant="caption" color={colors.grey[400]}>
            Select a file from your library to add to the club.
          </Typography>
        }
      />

      {/* ── Settings dialog ── */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Club Settings</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Club name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="Description"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
          />
          <TextField
            label="Color (hex)"
            value={editColor}
            onChange={(e) => setEditColor(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 7 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: editColor, mr: 1, flexShrink: 0 }} />
              ),
            }}
          />

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Invite link — share with people you want to invite
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', flex: 1 }}>
                {inviteUrl}
              </Typography>
              <Button size="small" onClick={handleCopyLink} sx={{ textTransform: 'none', flexShrink: 0 }}>
                {linkCopied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
            <Button
              size="small"
              color="warning"
              onClick={() => { handleRegenerateInvite(); setSettingsOpen(false); }}
              sx={{ textTransform: 'none', mt: 0.5 }}
            >
              Regenerate link (invalidates old one)
            </Button>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="error" display="block" mb={1} fontWeight="bold">
              Danger zone
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Delete club
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={settingsSaving}
          >
            {settingsSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirm dialog ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Club</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{club?.name}"? This action cannot be undone.
            All club files and data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteClub}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={!!snackMsg}
        autoHideDuration={3000}
        onClose={() => setSnackMsg('')}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
